import { ductNorm } from './duct-parse';
import type { DuctCalcType } from './ductwork';

/**
 * Bảng đơn giá ống gió của Báo giá dự toán (anh Tiến 27/10/2026: "Đơn giá: phân biệt Ống và Phụ kiện. Đơn giá ống thẳng
 * L1110: EI30, EI45, EI60, Ống tôn 0.75, 0.95, 1.15, 1.2, Ống inox. Đơn giá phụ kiện: co, tê, côn… là đơn giá ống × 20% hoặc tự nhập").
 * Giá theo m² (bộ tính CR-008 ra m²); phụ kiện để trống = giá ống thẳng × (1 + hệ số %), mặc định 20 %.
 */
export const DUCT_PRICE_CATEGORIES = [
  { key: 'EI30', label: 'Ống EI30' },
  { key: 'EI45', label: 'Ống EI45' },
  { key: 'EI60', label: 'Ống EI60' },
  { key: 'T075', label: 'Ống tôn 0.75' },
  { key: 'T095', label: 'Ống tôn 0.95' },
  { key: 'T115', label: 'Ống tôn 1.15' },
  { key: 'T120', label: 'Ống tôn 1.2' },
  { key: 'INOX', label: 'Ống inox' },
] as const;
export type DuctPriceCategory = (typeof DUCT_PRICE_CATEGORIES)[number]['key'];
export const DUCT_PRICE_CATEGORY_KEYS = DUCT_PRICE_CATEGORIES.map((c) => c.key) as [DuctPriceCategory, ...DuctPriceCategory[]];
export const DUCT_FITTING_MARKUP_DEFAULT = 20;

const TON: [number, DuctPriceCategory][] = [
  [0.75, 'T075'],
  [0.95, 'T095'],
  [1.15, 'T115'],
  [1.2, 'T120'],
];

export const ductPriceLabel = (c: DuctPriceCategory) => DUCT_PRICE_CATEGORIES.find((x) => x.key === c)!.label;

/** Ống thẳng = "Ống"; co, tê, côn, bầu giảm, lượn, vuông tròn, bịt đầu = "Phụ kiện" */
export const isDuctFitting = (calcType: DuctCalcType) => calcType !== 'rect_straight' && calcType !== 'round_straight';

/**
 * Nhóm giá của dòng: có EI → EI30 / EI45 / EI60 (EI ≤ 30 → EI30, ≤ 45 → EI45, ≤ 60 → EI60; trên 60 chưa có nhóm);
 * chữ inox / SUS / 304 / 316 → Inox (mọi độ dày); còn lại tôn theo độ dày dòng ghi, không ghi thì theo mã, mặc định 0,75 mm.
 */
export function ductPriceCategory(d: { ei: number | null; thicknessMm: number | null }, text: string, productThicknessMm?: number | null): DuctPriceCategory | null {
  if (d.ei) return d.ei <= 30 ? 'EI30' : d.ei <= 45 ? 'EI45' : d.ei <= 60 ? 'EI60' : null;
  if (/\b(inox|sus|304|316)\b/.test(ductNorm(text))) return 'INOX';
  const t = d.thicknessMm ?? productThicknessMm ?? 0.75;
  return TON.find(([v]) => Math.abs(v - t) < 1e-6)?.[1] ?? null;
}

export interface DuctPriceRow {
  category: DuctPriceCategory;
  pipePrice: number | null;
  fittingPrice: number | null;
}

/** Giá / m² theo bảng: ống = giá ống thẳng; phụ kiện = giá phụ kiện gõ riêng, trống thì giá ống × (1 + hệ số %) */
export function ductTablePrice(
  row: DuctPriceRow | undefined,
  fitting: boolean,
  markupPercent: number,
): { price: number; how: string } | null {
  if (!row) return null;
  if (!fitting) return row.pipePrice ? { price: row.pipePrice, how: 'giá ống thẳng' } : null;
  if (row.fittingPrice) return { price: row.fittingPrice, how: 'giá phụ kiện gõ riêng' };
  if (!row.pipePrice) return null;
  return { price: Math.round(row.pipePrice * (1 + markupPercent / 100)), how: `giá ống × (1 + ${markupPercent}%)` };
}
